import { useState } from 'react';
import { toast } from 'react-toastify';
import { LoadingSpinner, QueryErrorAlert } from '@/shared/components';
import { useStartDraftAction } from '../../hooks/useStartDraftAction';
import { useTemplateVersionsQuery } from '../../hooks/useTemplateVersionsQuery';
import { defaultVersionId } from '../../utils/versionLabels';
import { NoVersions } from './NoVersions';
import { VersionDetail } from './VersionDetail';
import { VersionList } from './VersionList';

interface Props {
  templateId: string;
  hasDraft: boolean;
  canRestore?: boolean;
  onRestored: () => void;
  onOpenDraft?: () => void;
}

export function VersionHistoryPanel({ templateId, hasDraft, canRestore = true, onRestored, onOpenDraft }: Props) {
  const [page, setPage] = useState(1);
  const [perPage, setPerPage] = useState(10);
  const [selectedId, setSelectedId] = useState<string | null>(null);
  const query = useTemplateVersionsQuery(templateId, page, perPage);
  const { start, isStarting } = useStartDraftAction(templateId);

  if (query.isLoading) {
    return <LoadingSpinner size="xl" className="py-12" message="Loading versions..." />;
  }

  if (query.isError || !query.data) {
    return (
      <QueryErrorAlert message="Could not load the version history." onRetry={() => query.refetch()} isRetrying={query.isFetching} />
    );
  }

  const { versions, meta } = query.data;
  if (versions.length === 0) {
    return <NoVersions canStart={canRestore} isStarting={isStarting} onStart={() => start(undefined, onRestored)} />;
  }

  const selected = versions.find((version) => version.id === selectedId) ?? versions.find((version) => version.id === defaultVersionId(versions));
  const restore = (versionId: string) =>
    start(versionId, () => {
      toast.success('Draft created from the selected version.');
      onRestored();
    });

  return (
    <div className="grid items-start gap-6 xl:grid-cols-3">
      <VersionList
        versions={versions}
        meta={meta}
        selectedId={selected?.id ?? null}
        onSelect={setSelectedId}
        onPageChange={setPage}
        onPerPageChange={(next) => {
          setPerPage(next);
          setPage(1);
        }}
      />
      {selected && (
        <div className="min-w-0 xl:col-span-2">
          <VersionDetail
          key={selected.id}
          templateId={templateId}
          version={selected}
          hasDraft={hasDraft}
          canRestore={canRestore}
          isRestoring={isStarting}
          onRestore={restore}
          onOpenDraft={onOpenDraft}
          />
        </div>
      )}
    </div>
  );
}
